import * as React from 'react';
import { startTransition, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { styled, useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import {
  Blocks,
  HelpCircleIcon,
  LogOutIcon,
  Settings,
  Sun,
} from 'lucide-react';

import {
  IcArrowDown,
  IcMenuClose,
  IcMenuOpen,
  IcProfiles,
} from '@/assets/images';
import { NotificationBell } from '@/components';
import { OfficeChatButton } from '@/components/OfficeChat';
import { LocalStorageItems } from '@/constants';
import { useAuthContext } from '@/hooks';
import { FirstResponderItemsKeys, ISideBarItem } from '@/layouts/data';
import { IAccount, IAgency, IUser, Role } from '@/models';
import { getAllowedSidebarItems, isTargetRole } from '@/services';
import {
  useUserStore,
  useSidebarStore,
  useAgencyStore,
  useOfficeChatStore,
} from '@/store';
import { sizes } from '@/theme/variables';
import { formatPersonName } from '@/utils';

import EnvironmentBadge from '../EnvironmentBadge/EnvironmentBadge';
import { ThemeSwitch } from '../ThemeSwitch';
import {
  Drawer,
  StyledListItemButton,
  StyledListItemIcon,
  StyledListItemText,
  ToggleButton,
} from './styles';

const TooltipContainer = styled('div')({});

type ListItemComponentProps = {
  label?: string;
  icon?: React.ElementType;
  open: boolean;
  link?: string;
  onClick?: () => void;
  items?: ISideBarItem[];
  showTooltip?: boolean;
};

const ListItemComponent = ({
  label,
  icon,
  open,
  link,
  onClick,
  items,
  isActive,
  showTooltip = true,
}: ListItemComponentProps & { isActive: boolean | string | undefined }) => {
  const navigate = useNavigate();
  const { account } = useUserStore();
  const t = useTheme();
  const { pathname } = useLocation();
  const [submenuOpen, setSubmenuOpen] = React.useState(false);
  const [submenuEl, setSubmenuEl] = React.useState<HTMLElement | undefined>();
  const activeLink = pathname;

  const isActiveLink = (l?: string) => {
    if (l === '/admin' && activeLink.startsWith('/agencies/')) {
      return true;
    }

    return l && (activeLink === l || activeLink.startsWith(`${l}/`));
  };

  const onClickMenuItem = (itemLink?: string) => {
    setSubmenuEl(undefined);
    if (itemLink) startTransition(() => navigate(itemLink));
  };

  // Filter out items based on demo mode setting
  items = items?.filter(({ isDemoMode }) =>
    isDemoMode ? account?.settings.demoMode : true,
  );

  const menuItems = () => {
    const renderNestedItems = (
      menuItemsList: ISideBarItem[],
      level = 0,
    ): React.ReactNode[] => {
      return menuItemsList.flatMap((item) => {
        const hasChildren = item.items && item.items.length > 0;

        const menuItem = (
          <TooltipTitle
            key={item.key}
            className={clsx(isActiveLink(item.link) && 'active')}
            onClick={(e) => {
              e.stopPropagation();
              if (!hasChildren) {
                onClickMenuItem(item.link);
              }
            }}
            sx={{
              paddingLeft: `${1 + level * 1.5}rem`,
              cursor: hasChildren ? 'default' : 'pointer',
              opacity: hasChildren ? 0.9 : 1,
            }}
          >
            {item.label}
          </TooltipTitle>
        );

        if (hasChildren && item.items) {
          return [menuItem, ...renderNestedItems(item.items, level + 1)];
        }

        return [menuItem];
      });
    };

    if (!items) return null;

    return renderNestedItems(items);
  };

  const handleNavItemAction = (e: any) => {
    e.preventDefault();
    if (!items && link) {
      startTransition(() => navigate(link));
      return;
    }
    if (items && open) setSubmenuOpen(!submenuOpen);
    if (items && !open) {
      if (submenuEl) setSubmenuEl(undefined);
      else setSubmenuEl(e.currentTarget);
    }
  };

  const TooltipTitle = styled(MenuItem)(({ theme }) => ({
    padding: '1rem',
    backgroundColor: theme.palette.background.paper,

    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },

    '&.active': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.error.main
          : theme.palette.sideBar.main,
      color: theme.palette.common.white,
    },
  }));

  const content = (
    <ListItem disablePadding sx={{ display: 'block' }} onClick={onClick}>
      <StyledListItemButton
        onClick={handleNavItemAction}
        className={clsx(isActive && 'active')}
        sx={[
          {
            borderRadius: 2,
            minHeight: 48,
            px: 2.5,
          },
          open
            ? {
                justifyContent: 'initial',
              }
            : {
                justifyContent: 'center',
              },
        ]}
      >
        <StyledListItemIcon
          className={clsx(isActive && 'active')}
          sx={[
            {
              minWidth: 0,
              justifyContent: 'center',
            },
            open
              ? {
                  mr: 3,
                }
              : {
                  mr: 'auto',
                },
          ]}
        >
          {icon && React.createElement(icon)}
        </StyledListItemIcon>
        <StyledListItemText
          className={clsx(isActive && 'active')}
          primary={label}
          sx={[
            open
              ? {
                  opacity: 1,
                }
              : {
                  opacity: 0,
                },
          ]}
        />
        {items && items?.length > 0 ? (
          <IcArrowDown color={isActive ? t.palette.common.white : ''} />
        ) : null}
      </StyledListItemButton>
    </ListItem>
  );

  return (
    <>
      <Tooltip
        disableFocusListener
        title={
          showTooltip ? (
            open ? null : items ? (
              menuItems()
            ) : (
              <TooltipTitle className="title">{label}</TooltipTitle>
            )
          ) : (
            ''
          )
        }
        placement="right-start"
        components={{ Tooltip: TooltipContainer }}
      >
        {link ? (
          <Link
            to={link}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
            }}
            onClick={(e) => e.preventDefault()}
          >
            {content}
          </Link>
        ) : (
          content
        )}
      </Tooltip>
      {submenuOpen && open && items && (
        <List component="div" disablePadding sx={{ p: 1 }}>
          {items.map((subItem) => (
            <ListItemComponent
              key={subItem.key}
              icon={subItem.icon}
              link={subItem.link}
              label={subItem.label}
              items={subItem.items}
              open={open}
              isActive={isActiveLink(subItem.link)}
            />
          ))}
        </List>
      )}
    </>
  );
};

const nestedItemStyles = {
  height: '50px',
  width: '260px',
  justifyContent: 'flex-start',
  paddingX: '8px',
  borderRadius: '6px',
};

// if user is first responder and has more than 2 roles we need to have switch for full version
const getHasMultipleViews = (user: IUser) => {
  return isTargetRole(user, Role.FIRST_RESPONDER) && user.roles.length > 2;
};

const ProfileButton = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '22px 8px 8px 22px',
  padding: '1px',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#3E5A6A0F',
  },
}));

export default function MainSideBar() {
  const { logout } = useAuthContext();
  const { user, account, userAgencyId } = useUserStore();
  const navigate = useNavigate();
  const { agencies } = useAgencyStore();
  const { hasAccess: hasOfficeChatAccess } = useOfficeChatStore();
  const { pathname } = useLocation();
  const t = useTheme();
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);

  const initialFullVersion = localStorage.getItem(
    LocalStorageItems.FullVersionSidebar,
  );

  const [isFullVersion, setIsFullVersion] = useState<boolean>(
    initialFullVersion === null || initialFullVersion === 'true',
  );

  const [currentAgency, setCurrentAgency] = useState<IAgency | null>(null);

  useEffect(() => {
    if (agencies.length > 0) {
      const agency = agencies.find(
        (item: IAgency) => item._id === userAgencyId,
      );
      setCurrentAgency(agency || null);
    }
  }, [userAgencyId, agencies]);

  // Use the sidebar store instead of local state
  const { isOpen: open, toggle } = useSidebarStore();

  const [hasMultipleViews, setHasMultipleViews] = useState(true);
  const allowedSidebarItems = getAllowedSidebarItems(
    user as IUser,
    account as IAccount,
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) {
        return;
      }
      if (e.code === 'BracketLeft' || e.key === '[') {
        e.preventDefault();
        useSidebarStore.getState().toggle();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    setHasMultipleViews(getHasMultipleViews(user as IUser));
  }, [user]);

  const { firstResponderSidebarItems, otherAllowedSidebarItems } =
    allowedSidebarItems.reduce(
      (
        acc: {
          firstResponderSidebarItems: ISideBarItem[];
          otherAllowedSidebarItems: ISideBarItem[];
        },
        item,
      ) => {
        if (FirstResponderItemsKeys.includes(item.key)) {
          acc.firstResponderSidebarItems.push(item);
        } else {
          acc.otherAllowedSidebarItems.push(item);
        }
        return acc;
      },
      { firstResponderSidebarItems: [], otherAllowedSidebarItems: [] },
    );

  const openMenu = Boolean(menuEl);

  const handleMenuClose = () => {
    setMenuEl(null);
  };

  const handleDrawerToggle = toggle;

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFullVersion(event.target.checked);
    localStorage.setItem(
      LocalStorageItems.FullVersionSidebar,
      event.target.checked ? 'true' : 'false',
    );
  };

  const isActiveLink = (l?: string) => {
    if (l === '/admin' && pathname.startsWith('/agencies/')) {
      return true;
    }

    return l && (pathname === l || pathname.startsWith(`${l}/`));
  };

  const StyledMenu = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
      backgroundColor: theme.palette.sideBar.light,
    },
  }));

  const dividerRef = React.useRef<HTMLHRElement>(null);
  const [toggleButtonTop, setToggleButtonTop] = React.useState(105);

  React.useEffect(() => {
    const updatePosition = () => {
      if (dividerRef.current) {
        const dividerRect = dividerRef.current.getBoundingClientRect();
        const drawerPaper = dividerRef.current.closest('.MuiDrawer-paper');

        if (drawerPaper) {
          const sidebarRect = drawerPaper.getBoundingClientRect();
          const relativeTop =
            dividerRect.top - sidebarRect.top + dividerRect.height / 2;
          setToggleButtonTop(relativeTop);
        }
      }
    };

    updatePosition();

    const drawerPaper = dividerRef.current?.closest('.MuiDrawer-paper');

    let resizeObserver: ResizeObserver | null = null;

    if (drawerPaper) {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updatePosition);
      });
      resizeObserver.observe(drawerPaper);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [open, hasMultipleViews, isFullVersion]);

  return (
    <Box
      sx={{
        display: 'flex',
        '&:hover .toggle-button': {
          opacity: 1,
        },
      }}
    >
      <Drawer variant="permanent" open={open}>
        <List sx={{ p: 2 }}>
          <EnvironmentBadge open={open} />
          <Divider sx={{ my: 1 }} />
          {(!hasMultipleViews || !isFullVersion) &&
            firstResponderSidebarItems.map((item) => (
              <div key={item.key}>
                <ListItemComponent
                  key={item.key}
                  icon={item.icon}
                  link={item.link}
                  label={item.label}
                  items={item.items}
                  open={open}
                  isActive={isActiveLink(item.link)}
                />
              </div>
            ))}
          <Divider
            ref={dividerRef}
            sx={{
              my: 1,
              visibility: 'hidden',
              height: 0,
              margin: 0,
            }}
          />
          <ToggleButton
            className="toggle-button"
            onClick={handleDrawerToggle}
            sx={{
              top: `${toggleButtonTop}px`,
              left: open
                ? `calc(${sizes.sidebarOpen}px - 18px)`
                : `calc(${sizes.sidebarClosed}px - 18px)`,
              opacity: 0,
              '&:hover': {
                opacity: 1,
              },
            }}
          >
            {open ? <IcMenuOpen /> : <IcMenuClose />}
          </ToggleButton>

          {isFullVersion &&
            otherAllowedSidebarItems.map((item) => (
              <div key={item.key}>
                <ListItemComponent
                  key={item.key}
                  icon={item.icon}
                  link={item.link}
                  label={item.label}
                  items={item.items}
                  open={open}
                  isActive={isActiveLink(item.link)}
                />
                {!isTargetRole(user as IUser, Role.NCIC_AUTHORIZED) &&
                  item.key === 'cfs-active' && <Divider sx={{ my: 1 }} />}
                {item.key === 'ncic-search' && <Divider sx={{ my: 1 }} />}
              </div>
            ))}
        </List>

        <Stack
          sx={{
            justifyContent: 'flex-end',
            flex: 1,
          }}
        >
          <Box sx={{ px: 2, pb: 1 }}>
            {hasOfficeChatAccess && <Divider sx={{ my: 1 }} />}

            <OfficeChatButton collapsed={!open} />

            {!open && <NotificationBell collapsed showLabel />}
          </Box>

          {/* Profile Section */}
          <Box sx={{ px: 2 }}>
            <Box
              sx={{
                pt: 2,
                pb: 2,
                borderTop: `1px solid ${t.palette.divider}`,
              }}
            >
              {open ? (
                <Stack direction="row" alignItems="center" gap={1}>
                  <ProfileButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuEl(e.currentTarget);
                    }}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                      }}
                    >
                      <IcProfiles />
                    </Box>
                    <Stack flex={1} minWidth={0}>
                      <Typography fontSize={15} fontWeight={500} noWrap>
                        {user?.firstName || user?.lastName ? (
                          <>
                            {formatPersonName(user)}
                            <span className="text-red-500">
                              &nbsp;&#183;&nbsp;
                            </span>
                          </>
                        ) : null}
                        {currentAgency?.code}
                      </Typography>
                      <Typography fontSize={13} fontWeight={400} noWrap>
                        {user?.email}
                      </Typography>
                    </Stack>
                  </ProfileButton>
                  <NotificationBell />
                </Stack>
              ) : (
                <Stack alignItems="center">
                  <div
                    className="cursor-pointer"
                    onClick={(e) => setMenuEl(e.currentTarget)}
                  >
                    <IcProfiles />
                  </div>
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>

        <StyledMenu
          id="setting-menu"
          anchorEl={menuEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ ml: 2 }}
        >
          <MenuItem
            sx={nestedItemStyles}
            onClick={() => {
              navigate('/settings/profile');
              handleMenuClose();
            }}
          >
            <div className="flex items-center gap-1">
              <Settings strokeWidth={1.5} /> Settings
            </div>
          </MenuItem>
          <Divider />
          <MenuItem sx={nestedItemStyles}>
            <Sun strokeWidth={1.5} className="mr-1" />
            <ThemeSwitch switchMode="radio" />
          </MenuItem>
          {hasMultipleViews && (
            <MenuItem sx={nestedItemStyles}>
              <Blocks strokeWidth={1.5} className="mr-1" />
              <Stack
                flex={1}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontWeight={500}>Full version</Typography>
                <Switch
                  color="error"
                  checked={isFullVersion}
                  onChange={handleSwitchChange}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </Stack>
            </MenuItem>
          )}
          <Divider />
          <MenuItem
            sx={nestedItemStyles}
            onClick={() => {
              navigate('/help');
              handleMenuClose();
            }}
          >
            <div className="flex items-center gap-1">
              <HelpCircleIcon strokeWidth={1.5} /> Help
            </div>
          </MenuItem>
          <Divider />
          <MenuItem sx={nestedItemStyles} onClick={() => logout()}>
            <div className="flex items-center gap-1 text-red-500">
              <LogOutIcon strokeWidth={1.5} /> Log out
            </div>
          </MenuItem>
        </StyledMenu>
      </Drawer>
    </Box>
  );
}
